import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import Footer from 'examples/Footer';
import MetricCard from 'components/MetricCard';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';

import {
  getSessionSnapshot, captureSnapshotBalance,
  getRoomSummary, getRoomNetMetrics, getRoomBlocks,
  getRoomBids, getRoomBossCycles, getRoomAirdropHistory,
  downloadSessionCSV, downloadSessionXLSX, getSessionReportUrl
} from 'services/nh-room-dashboard';
import {
  useDashboardTheme, ACCENT_COLOR, accentBarSx, cardSx,
  panelHeaderSx, countBadgeSx, tableHeadRowSx, tableRowSx, scrollbarSx,
  actionButtonSx, fmt, fmtBtc, fmtDate, fmtTimeShort, computePnl
} from 'layouts/nh-room-dashboard/utils';

// ── Reused live-dashboard components ──────────────────────────────────────────
import BlockMiningHistory from 'layouts/nh-room-dashboard/components/BlockMiningHistory';
import RecentBidsFeed from 'layouts/nh-room-dashboard/components/RecentBidsFeed';
import AirdropDistributionHistory from 'layouts/nh-room-dashboard/components/AirdropDistributionHistory';
import NetPLBreakdown from 'layouts/nh-room-dashboard/components/NetPLBreakdown';
import DashboardCharts from 'layouts/nh-room-dashboard/components/DashboardCharts';
import RewardDistributionPie from 'layouts/nh-room-dashboard/components/RewardDistributionPie';


// ── Inline: Snapshot Boss Panel (matches live BossCyclePanel layout) ──────────
function SnapshotBossPanel({ snapshot, bossCycles, loading, t }) {
  const boss = snapshot?.bossState;
  const cycleData = bossCycles?.data || [];

  return (
    <Card sx={cardSx}>
      <MDBox sx={panelHeaderSx(t)}>
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
          Boss Cycle
        </MDTypography>
      </MDBox>

      {/* Boss state at session end */}
      {boss?.userId ? (
        <MDBox sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
          <MDBox display='flex' alignItems='center' gap={1} mb={0.5}>
            <Chip label='Final Boss' color='warning' size='small' sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            <MDTypography variant='body2' fontWeight='bold' sx={{ color: '#fbbf24' }}>
              {boss.name}
            </MDTypography>
          </MDBox>
          <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
            Winning Bid: ${fmt(boss.winningBid)} &nbsp;|&nbsp; Winnings: ${fmt(boss.winnings)}
            {boss.highestBid != null && (<>&nbsp;|&nbsp; Highest: ${fmt(boss.highestBid)} / {fmtBtc(boss.highestBidBtc)} BTC</>)}
          </MDTypography>
          {boss.topBidders?.length > 0 && (
            <MDBox mt={1}>
              <MDTypography variant='caption' fontWeight='medium' sx={{ color: t.primaryText }}>
                Top Bidders:
              </MDTypography>
              {boss.topBidders.map((tb, i) => (
                <MDTypography key={tb.userId} variant='caption' display='block' sx={{ pl: 1, color: t.secondaryText }}>
                  {i + 1}. {tb.name} — ${fmt(tb.bid)}
                </MDTypography>
              ))}
            </MDBox>
          )}
        </MDBox>
      ) : (
        <MDBox sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
          <MDTypography variant='body2' sx={{ color: t.secondaryText, opacity: 0.6 }}>No boss at session end</MDTypography>
        </MDBox>
      )}

      {/* Boss cycle history from PG */}
      {cycleData.length > 0 && (
        <MDBox sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: `1px solid ${t.divider}` }}>
          <MDTypography variant='subtitle2' fontWeight='bold' mb={1} sx={{ fontSize: '0.85rem', color: t.primaryText }}>
            Boss Cycle History ({bossCycles?.total ?? 0})
          </MDTypography>
          {loading && cycleData.length === 0 ? (
            <Skeleton variant='rectangular' height={120} sx={{ borderRadius: '8px' }} />
          ) : (
            <TableContainer sx={{ ...scrollbarSx(t), maxHeight: 240 }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow sx={tableHeadRowSx(t)}>
                    <TableCell>Time</TableCell>
                    <TableCell>Boss</TableCell>
                    <TableCell align='right'>Bid ($)</TableCell>
                    <TableCell align='right'>Winnings ($)</TableCell>
                    <TableCell align='right'>Bidders</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cycleData.map((cycle, index) => (
                    <TableRow key={cycle.id} sx={tableRowSx(t, { index })}>
                      <TableCell>
                        <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText }}>{fmtTimeShort(cycle.cycleEndedAt)}</MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.78rem', color: t.primaryText }}>{cycle.bossName || '—'}</MDTypography>
                      </TableCell>
                      <TableCell align='right'>
                        <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(cycle.winningBidUsd)}</MDTypography>
                      </TableCell>
                      <TableCell align='right'>
                        <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(cycle.bossWinningsUsd)}</MDTypography>
                      </TableCell>
                      <TableCell align='right'>
                        <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{cycle.topBidders?.length ?? 0}</MDTypography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </MDBox>
      )}
    </Card>
  );
}

// ── Inline: Snapshot Train Leaderboard ────────────────────────────────────────
function SnapshotLeaderboard({ leaderboard, t }) {
  if (!leaderboard || leaderboard.length === 0) return null;

  return (
    <Card sx={cardSx}>
      <MDBox sx={panelHeaderSx(t)}>
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
          Train Leaderboard (at session end)
        </MDTypography>
        <MDBox sx={countBadgeSx(t)}>
          <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.65rem', color: t.tableHeaderColor, letterSpacing: '0.04em' }}>
            {leaderboard.length} player{leaderboard.length !== 1 ? 's' : ''}
          </MDTypography>
        </MDBox>
      </MDBox>
      <TableContainer sx={scrollbarSx(t)}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow sx={tableHeadRowSx(t)}>
              <TableCell sx={{ width: 28, pr: 0 }}>#</TableCell>
              <TableCell>Player</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderboard.map((entry, i) => {
              const isTop = i === 0;
              return (
                <TableRow key={entry.userId} sx={tableRowSx(t, { index: i, isTop })}>
                  <TableCell sx={{ width: 28, pr: 0 }}>
                    <MDTypography variant='caption' sx={{ fontSize: '0.65rem', color: isTop ? t.rankTopColor : t.rankColor, fontWeight: isTop ? 700 : 400 }}>
                      {entry.position ?? (i + 1)}
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDTypography variant='caption' fontWeight={isTop ? 'bold' : 'medium'} sx={{ fontSize: '0.78rem', color: t.primaryText }}>
                      {entry.name}
                    </MDTypography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

// ── Inline: Cumulative Rewards Table ──────────────────────────────────────────
function SnapshotRewardsBoards({ rewardsBoards, t }) {
  if (!rewardsBoards || rewardsBoards.length === 0) return null;

  return (
    <Card sx={cardSx}>
      <MDBox sx={panelHeaderSx(t)}>
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
          Cumulative Rewards
        </MDTypography>
      </MDBox>
      <TableContainer sx={scrollbarSx(t)}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow sx={tableHeadRowSx(t)}>
              <TableCell>Coin</TableCell>
              <TableCell align='right'>Amount (Coin)</TableCell>
              <TableCell align='right'>Amount (BTC)</TableCell>
              <TableCell align='right'>Amount (USD)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rewardsBoards.slice().sort((a, b) => (Number(b.amountUsd) || 0) - (Number(a.amountUsd) || 0)).map((rb, i) => (
              <TableRow key={i} sx={tableRowSx(t, { index: i, isTop: i === 0 })}>
                <TableCell>
                  <MDTypography variant='caption' fontWeight={i === 0 ? 'bold' : 'medium'} sx={{ fontSize: '0.78rem', color: t.primaryText }}>
                    {rb.currency}
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{fmtBtc(rb.amountCoin)}</MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{fmtBtc(rb.amountBtc)}</MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(rb.amountUsd)}</MDTypography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Main Page Component
// ═════════════════════════════════════════════════════════════════════════════

function NHSessionDetail() {
  const { snapshotId } = useParams();
  const navigate = useNavigate();
  const t = useDashboardTheme();

  // ── Snapshot state ──────────────────────────────────────────────────────
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  const [exportingXLSX, setExportingXLSX] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  // ── PG-sourced state (fetched by roomId once snapshot loads) ────────────
  const [summary, setSummary] = useState(null);
  const [netMetrics, setNetMetrics] = useState(null);
  const [blocks, setBlocks] = useState({ data: [], total: 0 });
  const [bids, setBids] = useState({ data: [], total: 0 });
  const [bossCycles, setBossCycles] = useState({ data: [], total: 0 });
  const [airdropHistory, setAirdropHistory] = useState({ data: [], total: 0 });
  const [pgLoading, setPgLoading] = useState(true);

  // ── Fetch snapshot ──────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await getSessionSnapshot(snapshotId);
      setSnapshot(data);
      setLoading(false);
    })();
  }, [snapshotId]);

  // ── Fetch PG data once we have the roomId from the snapshot ─────────────
  const snapshotRoomId = snapshot?.roomId || null;
  useEffect(() => {
    if (!snapshotRoomId) return;
    setPgLoading(true);
    Promise.all([
      getRoomSummary(snapshotRoomId),
      getRoomNetMetrics(snapshotRoomId, 'minute'),
      getRoomBlocks(snapshotRoomId, 50, 0),
      getRoomBids(snapshotRoomId, 50, 0),
      getRoomBossCycles(snapshotRoomId, 20, 0),
      getRoomAirdropHistory(snapshotRoomId, 20, 0)
    ]).then(([sm, nm, bl, bd, bc, ah]) => {
      if (sm) setSummary(sm);
      if (nm) setNetMetrics(nm);
      if (bl) setBlocks(bl);
      if (bd) setBids(bd);
      if (bc) setBossCycles(bc);
      if (ah) setAirdropHistory(ah);
      setPgLoading(false);
    }).catch((err) => {
      console.error('PG data fetch error:', err);
      setPgLoading(false);
    });
  }, [snapshotRoomId]);

  const handleCaptureBalance = async () => {
    setCapturing(true);
    const result = await captureSnapshotBalance(snapshotId);
    if (result?.captured) {
      const refreshed = await getSessionSnapshot(snapshotId);
      if (refreshed) setSnapshot(refreshed);
    }
    setCapturing(false);
  };

  const handleExportCSV = async () => {
    setExportingCSV(true);
    const ok = await downloadSessionCSV(snapshotId);
    if (!ok) setSnackMsg('Failed to download CSV export');
    setExportingCSV(false);
  };

  const handleExportXLSX = async () => {
    setExportingXLSX(true);
    const ok = await downloadSessionXLSX(snapshotId);
    if (!ok) setSnackMsg('Failed to download XLSX export');
    setExportingXLSX(false);
  };

  const handleShareReport = () => {
    const url = getSessionReportUrl(snapshotId);
    navigator.clipboard.writeText(url).then(() => {
      setSnackMsg('Report link copied to clipboard');
    }).catch(() => {
      setSnackMsg('Could not copy link');
    });
    window.open(url, '_blank');
  };

  // ── Loading state ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={{ xs: 1, md: 3 }}>
          <Card sx={{ ...cardSx, mb: 3 }}>
            <MDBox sx={accentBarSx} />
            <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
              <Skeleton variant='text' width='40%' height={36} />
              <Skeleton variant='text' width='25%' height={20} sx={{ mt: 1 }} />
            </MDBox>
          </Card>
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} lg={3} key={i}>
                <MetricCard title='Loading...' loading icon='hourglass_empty' />
              </Grid>
            ))}
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // ── Not found ───────────────────────────────────────────────────────────
  if (!snapshot) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={{ xs: 1, md: 3 }}>
          <Card sx={{ ...cardSx, p: 3 }}>
            <MDTypography variant='h6' color='error'>Session snapshot not found</MDTypography>
            <MDButton variant='outlined' color='info' size='small' onClick={() => navigate('/nh-sessions')} sx={{ mt: 2, borderRadius: '10px' }}>
              <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>Back to Sessions
            </MDButton>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // ── Derived metrics ─────────────────────────────────────────────────────
  const players = snapshot.players || [];
  const earnings = snapshot.earningsLeaderboard || [];
  const totalEarnings = earnings.reduce((s, e) => s + (Number(e.totalEarnings) || 0), 0);
  const totalBlockRewardsEarned = earnings.reduce((s, e) => s + (Number(e.breakdown?.blockRewardUsd) || 0), 0);
  const totalBossEarned = earnings.reduce((s, e) => s + (Number(e.breakdown?.bossUsd) || 0), 0);
  const totalAirdropEarned = earnings.reduce((s, e) => s + (Number(e.breakdown?.airdropUsd) || 0), 0);
  const snapshotNetWagered = snapshot.netWageredUsd != null
    ? Number(snapshot.netWageredUsd)
    : Number(snapshot.totalBidsUsd) || 0;
  const rtp = snapshotNetWagered > 0 ? (totalEarnings / snapshotNetWagered) * 100 : null;

  // Operator P&L from summary (PG-sourced, works for ended sessions)
  const plBtc = netMetrics?.operatorPnlBtc ?? summary?.operatorPnlBtc ?? null;
  const plUsd = netMetrics?.operatorPnlUsd ?? summary?.operatorPnlUsd ?? null;

  // Placeholder text for PG-sourced cards: shows "Loading…" during fetch, "No data" after
  const pgPlaceholder = pgLoading ? 'Loading…' : 'No data';

  // Build a synthetic liveState-like object for RewardDistributionPie
  const rewardPercentagesObj = snapshot.rewardPercentages || null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1, md: 3 }}>
        {/* ── Header Card ──────────────────────────────────────────────── */}
        <Card sx={{ ...cardSx, mb: 3 }}>
          <MDBox sx={accentBarSx} />
          <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
            <MDBox display='flex' justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap='wrap' gap={2}>
              <MDBox display='flex' alignItems='center' gap={{ xs: 1.5, sm: 2 }}>
                <MDButton variant='outlined' color='info' size='small' onClick={() => navigate('/nh-sessions')} sx={{ minWidth: 0, px: 1, borderRadius: '10px' }}>
                  <Icon fontSize='small'>arrow_back</Icon>
                </MDButton>
                <MDBox
                  sx={{
                    width: { xs: '40px', sm: '48px' },
                    height: { xs: '40px', sm: '48px' },
                    borderRadius: '14px',
                    background: ACCENT_COLOR,
                    boxShadow: '0 4px 12px rgba(226,0,27,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Icon sx={{ color: 'white !important', fontSize: '1.5rem !important' }}>assessment</Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant='h5' fontWeight='bold' sx={{ letterSpacing: '-0.01em', lineHeight: 1.2, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                    Session Snapshot
                  </MDTypography>
                  <MDTypography variant='body2' color='text' sx={{ opacity: 0.6, fontSize: '0.8rem' }}>
                    {fmtDate(snapshot.createdAt)} &nbsp;|&nbsp; Stage: {snapshot.stage || '—'}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox display='flex' gap={1} flexWrap='wrap' alignItems='center'>
                <Chip label={snapshot.endReason || 'Ended'} size='small' sx={{ fontWeight: 600 }} />
                {snapshot.betLimitName && (
                  <Chip
                    label={`Preset: ${snapshot.betLimitName}${snapshot.betLimitMinBid != null ? ` (min ${Number(snapshot.betLimitMinBid).toFixed(8)} BTC)` : ''}`}
                    size='small'
                    variant='outlined'
                    color='info'
                  />
                )}
                <MDTypography variant='caption' sx={{ fontSize: '0.68rem', color: t.secondaryText, fontFamily: 'monospace' }}>
                  Room: {snapshot.roomId?.slice(0, 12)}…
                </MDTypography>
                <MDBox display='flex' gap={0.75} ml={{ xs: 0, sm: 1 }}>
                  {[
                    { tip: 'Download CSV (ZIP)', icon: 'download', label: exportingCSV ? '…' : 'CSV', onClick: handleExportCSV, disabled: exportingCSV },
                    { tip: 'Download Excel (XLSX)', icon: 'table_chart', label: exportingXLSX ? '…' : 'XLSX', onClick: handleExportXLSX, disabled: exportingXLSX },
                    { tip: 'Open shareable report & copy link', icon: 'share', label: 'Share', onClick: handleShareReport, disabled: false, accent: t.isDark ? '#4caf50' : '#2e7d32' },
                  ].map((btn) => (
                    <Tooltip key={btn.icon} title={btn.tip}>
                      <MDBox
                        component='button'
                        onClick={btn.disabled ? undefined : btn.onClick}
                        disabled={btn.disabled}
                        sx={actionButtonSx(t, { accent: btn.accent, disabled: btn.disabled })}
                      >
                        <Icon sx={{ fontSize: '0.88rem !important', color: 'inherit' }}>{btn.icon}</Icon>
                        {btn.label}
                      </MDBox>
                    </Tooltip>
                  ))}
                </MDBox>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>

        <Snackbar
          open={!!snackMsg}
          autoHideDuration={3000}
          onClose={() => setSnackMsg('')}
          message={snackMsg}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />

        {/* Last Wins Ticker removed from snapshot page — only shown on live dashboard */}

        {/* ── Key Metrics ──────────────────────────────────────────────── */}
        <MDBox mb={3}>
          <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
            <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: ACCENT_COLOR, flexShrink: 0 }} />
            <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: t.primaryText, fontSize: '0.95rem' }}>
              Key Metrics
            </MDTypography>
            <MDBox sx={{ flex: 1, height: '1px', background: t.divider, display: { xs: 'none', sm: 'block' } }} />
          </MDBox>
          <Grid container spacing={3}>
            {/* Row 1: Core session metrics */}
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard title='Players' tooltip='Number of unique players who participated in this session.' value={`${snapshot.playerCount}`} icon='people' iconColor='info' />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard title='Total Bids' tooltip='Total bids placed (normal + auto). Turnover shows gross USD handle (excludes boss bids).' value={`${snapshot.totalBidsCount}`} subtitle={`$${fmt(snapshot.totalBidsUsd)} USD turnover`} icon='casino' iconColor='primary' />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Total Blocks Mined'
                tooltip='Number of cryptocurrency blocks mined by the NiceHash engine during this session, with the total reward value received.'
                value={summary ? `${summary.totalBlocksMined}` : null}
                placeholder={!summary}
                placeholderText={pgPlaceholder}
                subtitle={summary ? `${fmtBtc(summary.totalBlockRewardBtc)} BTC / $${fmt(summary.totalBlockRewardUsd)}` : ''}
                icon='diamond'
                iconColor='warning'
                loading={pgLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Player RTP'
                tooltip='Return To Player: total player earnings divided by net wagered, as a percentage. Values above 100% mean players earned more than they wagered.'
                value={rtp != null ? `${rtp.toFixed(2)}%` : null}
                placeholder={rtp == null}
                placeholderText='No wagers'
                subtitle={rtp != null ? `$${fmt(totalEarnings)} returned / $${fmt(snapshotNetWagered)} wagered` : ''}
                icon='percent'
                iconColor={rtp != null && rtp <= 100 ? 'success' : rtp != null ? 'error' : 'info'}
              />
            </Grid>

            {/* Row 2: Earnings breakdown */}
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Total Earnings'
                tooltip='Sum of all player earnings: block reward shares + boss winnings + airdrops.'
                value={`$${fmt(totalEarnings)}`}
                subtitle={summary ? `${fmtBtc((Number(summary.totalBlockSharesBtc) || 0) + (Number(summary.totalBossWinningsBtc) || 0) + (Number(summary.totalAirdropsBtc) || 0))} BTC` : ''}
                icon='monetization_on'
                iconColor='success'
                loading={pgLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Block Rewards'
                tooltip='BTC paid to train position holders (1st/2nd/3rd) from each mined block. 100% of block rewards are distributed.'
                value={`$${fmt(totalBlockRewardsEarned)}`}
                subtitle={summary ? `${fmtBtc(summary.totalBlockSharesBtc)} BTC` : ''}
                icon='diamond'
                iconColor='warning'
                loading={pgLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Boss Payouts'
                tooltip='Total paid to boss winners from the boss reward share of each mined block.'
                value={`$${fmt(totalBossEarned)}`}
                subtitle={summary ? `${fmtBtc(summary.totalBossWinningsBtc)} BTC` : ''}
                icon='military_tech'
                iconColor='warning'
                loading={pgLoading}
              />
            </Grid>
            {(totalAirdropEarned > 0 || (summary && summary.totalAirdropsCount > 0)) && (
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  title='Airdrop Payouts'
                  tooltip='Total distributed to players via airdrop meter triggers.'
                  value={`$${fmt(totalAirdropEarned)}`}
                  subtitle={summary ? `${summary.totalAirdropsCount} airdrops / ${fmtBtc(summary.totalAirdropsBtc)} BTC` : ''}
                  icon='redeem'
                  iconColor='info'
                  loading={pgLoading}
                />
              </Grid>
            )}

            {/* Row 3: P&L / Engine / NH Balance / Jackpot */}
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Operator P&L'
                tooltip='Our profit/loss: Wagering Net (pure bid revenue) minus Engine Net Cost (deposits + refills minus returns). Block rewards and boss winnings are pass-through distributions and excluded.'
                value={plBtc != null ? `${fmtBtc(plBtc)} BTC` : null}
                placeholder={plBtc == null}
                placeholderText={pgPlaceholder}
                subtitle={plUsd != null ? `$${fmt(plUsd)}` : ''}
                icon='trending_up'
                iconColor={plBtc != null && plBtc >= 0 ? 'success' : 'error'}
                loading={pgLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Engine Return'
                tooltip='BTC returned from the NiceHash engine when it was stopped at session end. Equals unused hash budget + airdrop pot.'
                value={snapshot.engineRemainingBtc != null ? `${fmtBtc(snapshot.engineRemainingBtc)} BTC` : null}
                placeholder={snapshot.engineRemainingBtc == null}
                placeholderText='Not captured'
                icon='undo'
                iconColor='info'
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              {snapshot.nicehashBalanceBtc != null ? (
                <MetricCard
                  title='NH Closing Balance'
                  tooltip='NiceHash account balance captured shortly after session ended. Shared across all engines.'
                  value={`${fmtBtc(snapshot.nicehashBalanceBtc)} BTC`}
                  subtitle={snapshot.nicehashBalanceCapturedAt ? `Captured ${fmtDate(snapshot.nicehashBalanceCapturedAt)}` : ''}
                  icon='account_balance'
                  iconColor='dark'
                />
              ) : (
                <Card sx={cardSx}>
                  <MDBox sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.72rem', color: t.secondaryText, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      NH Closing Balance
                    </MDTypography>
                    {snapshot.nicehashBalanceError ? (
                      <Chip label={`Error: ${snapshot.nicehashBalanceError.slice(0, 40)}`} size='small' color='error' sx={{ fontSize: '0.65rem' }} />
                    ) : (
                      <Chip label='Not captured' size='small' sx={{ fontSize: '0.65rem' }} />
                    )}
                    <MDButton
                      variant='outlined'
                      color='info'
                      size='small'
                      disabled={capturing}
                      onClick={handleCaptureBalance}
                      sx={{ borderRadius: '8px', fontSize: '0.68rem', mt: 0.5 }}
                    >
                      <Icon sx={{ mr: 0.5, fontSize: '0.9rem !important' }}>sync</Icon>
                      {capturing ? 'Capturing...' : 'Capture Now'}
                    </MDButton>
                  </MDBox>
                </Card>
              )}
            </Grid>
            {snapshot.jackpot != null && Number(snapshot.jackpot) > 0 && (
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  title='Jackpot'
                  tooltip='Jackpot pool value at session end, accumulated from block rewards.'
                  value={`$${fmt(snapshot.jackpot)}`}
                  icon='emoji_events'
                  iconColor='error'
                />
              </Grid>
            )}
          </Grid>
        </MDBox>

        {/* ── Row: Two-Column Layout (matches live dashboard) ──────────── */}
        <Grid container spacing={2} mb={3}>
          {/* Left Column: Players & Bidding (7/12) */}
          <Grid item xs={12} lg={7}>
            <MDBox display='flex' flexDirection='column' gap={2}>
              {/* Train Position Leaderboard (Phase 1) */}
              <SnapshotLeaderboard leaderboard={snapshot.leaderboard} t={t} />

              {/* Player Earnings Table */}
              {players.length > 0 && (
                <Card sx={cardSx}>
                  <MDBox sx={panelHeaderSx(t)}>
                    <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
                      Player Earnings Breakdown
                    </MDTypography>
                    <MDBox sx={countBadgeSx(t)}>
                      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.65rem', color: t.tableHeaderColor, letterSpacing: '0.04em' }}>
                        {players.length} player{players.length !== 1 ? 's' : ''}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <TableContainer sx={scrollbarSx(t)}>
                    <Table size='small' stickyHeader>
                      <TableHead>
                        <TableRow sx={tableHeadRowSx(t)}>
                          <TableCell sx={{ width: 28, pr: 0 }}>#</TableCell>
                          <TableCell>Player</TableCell>
                          <TableCell align='right'>Bids</TableCell>
                          <TableCell align='right'>Bid Total ($)</TableCell>
                          <TableCell align='right'>Total Earnings ($)</TableCell>
                          <TableCell align='right'>Block ($)</TableCell>
                          <TableCell align='right'>Boss ($)</TableCell>
                          <TableCell align='right'>Airdrop ($)</TableCell>
                          <TableCell align='right'>P&L ($)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...players]
                          .sort((a, b) => (b.totalEarningsUsd || 0) - (a.totalEarningsUsd || 0))
                          .map((p, index) => {
                            const isTop = index === 0;
                            return (
                              <TableRow key={p.userId} sx={tableRowSx(t, { index, isTop })}>
                                <TableCell sx={{ width: 28, pr: 0 }}>
                                  <MDTypography variant='caption' sx={{ fontSize: '0.65rem', color: isTop ? t.rankTopColor : t.rankColor, fontWeight: isTop ? 700 : 400 }}>
                                    {index + 1}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDBox display='flex' alignItems='center' gap={0.5}>
                                    <MDTypography variant='caption' fontWeight={isTop ? 'bold' : 'medium'} sx={{ fontSize: '0.78rem', color: t.primaryText }}>
                                      {p.displayName}
                                    </MDTypography>
                                    <Tooltip title='Copy user ID' placement='top'>
                                      <IconButton size='small' onClick={() => { navigator.clipboard.writeText(p.userId).then(() => setSnackMsg('Copied ' + p.userId.slice(0, 12) + '…')).catch(() => setSnackMsg('Copy failed')); }} sx={{ p: '2px', color: t.secondaryText, '&:hover': { color: '#e2001b' } }}>
                                        <Icon sx={{ fontSize: '0.82rem !important' }}>content_copy</Icon>
                                      </IconButton>
                                    </Tooltip>
                                  </MDBox>
                                </TableCell>
                                <TableCell align='right'>
                                  <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{p.normalBidCount}</MDTypography>
                                </TableCell>
                                <TableCell align='right'>
                                  <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(p.normalBidTotal)}</MDTypography>
                                </TableCell>
                                <TableCell align='right'>
                                  <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(p.totalEarningsUsd)}</MDTypography>
                                </TableCell>
                                <TableCell align='right'>
                                  <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.secondaryText }}>${fmt(p.blockRewardUsd)}</MDTypography>
                                </TableCell>
                                <TableCell align='right'>
                                  <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.secondaryText }}>${fmt(p.bossUsd)}</MDTypography>
                                </TableCell>
                                <TableCell align='right'>
                                  <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.secondaryText }}>${fmt(p.airdropUsd)}</MDTypography>
                                </TableCell>
                                <TableCell align='right'>
                                  {(() => { const pnl = computePnl(p); return (
                                    <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: pnl >= 0 ? '#4caf50' : '#f44336' }}>
                                      {pnl >= 0 ? '' : '-'}${fmt(Math.abs(pnl))}
                                    </MDTypography>
                                  ); })()}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              )}

              {/* Recent Bids (Phase 2: from PG) */}
              <RecentBidsFeed bids={bids} loading={pgLoading} />

              {/* Boss Cycle (Phase 1: snapshot boss + Phase 2: PG history) */}
              <SnapshotBossPanel snapshot={snapshot} bossCycles={bossCycles} loading={pgLoading} t={t} />
            </MDBox>
          </Grid>

          {/* Right Column: Mining & Airdrop (5/12) */}
          <Grid item xs={12} lg={5}>
            <MDBox display='flex' flexDirection='column' gap={2}>
              {/* Cumulative Rewards (Phase 1: from snapshot) */}
              <SnapshotRewardsBoards rewardsBoards={snapshot.rewardsBoards} t={t} />

              {/* Block Mining History (Phase 2: from PG) */}
              <BlockMiningHistory blocks={blocks} loading={pgLoading} roomId={snapshotRoomId} knownCoins={snapshot.rewardsBoards?.map((rb) => rb.currency)} />

              {/* Airdrop Distribution History (Phase 2: from PG) */}
              {(airdropHistory?.total > 0 || airdropHistory?.data?.length > 0) && (
                <AirdropDistributionHistory airdropHistory={airdropHistory} loading={pgLoading} />
              )}
            </MDBox>
          </Grid>
        </Grid>

        {/* ── Row: Financial Summary & Charts (full width) ─────────────── */}
        <NetPLBreakdown
          netMetrics={netMetrics}
          summary={summary}
          engineInfo={null}
          liveState={null}
          nicehashBalance={null}
          loading={pgLoading}
        />

        {/* Reward Distribution Pie (Phase 3: from snapshot or PG) */}
        {rewardPercentagesObj && (
          <RewardDistributionPie
            liveState={{ rewardPercentages: rewardPercentagesObj }}
            loading={false}
          />
        )}

        <DashboardCharts netMetrics={netMetrics} loading={pgLoading} />
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NHSessionDetail;
